<!-- EntriesFilterBar.vue -->
<template>
    <v-row>
        <v-col cols="3" sm="1" md="1" lg="1">
            <v-btn
                icon
                size="large"
                elevation="3"
                variant="plain"
                @click="$emit('toggle-sort')"
                style="
                    width: 48px;
                    height: 48px;
                    min-width: 48px;
                    min-height: 48px;
                "
            >
                <v-icon>
                    {{
                        sortDirection === "desc"
                            ? "mdi-sort-clock-descending-outline"
                            : "mdi-sort-clock-ascending-outline"
                    }}
                </v-icon>
            </v-btn>
        </v-col>
        <v-col cols="9" sm="11" md="11" lg="11">
            <v-card>
                <v-toolbar
                    density="compact"
                    @click="isExpanded = !isExpanded"
                    class="cursor-pointer"
                    :color="isMobile && chips.length ? 'primary' : ''"
                >
                    <v-toolbar-title>{{
                        $t("entries.filters")
                    }}</v-toolbar-title>

                    <!-- show chips on desktop -->
                    <template v-if="chips.length && !isMobile">
                        <v-spacer></v-spacer>
                        <v-chip
                            v-for="chip in chips"
                            :key="chip.id"
                            closable
                            size="small"
                            color="primary"
                            @click.stop
                            @click:close="chip.clear()"
                            class="ma-1"
                        >
                            {{ chip.label }}
                        </v-chip>
                    </template>

                    <!-- CLEAR‑ALL X  (mobile & collapsed) -->
                    <template v-if="isMobile && chips.length && !isExpanded">
                        <v-spacer></v-spacer>
                        <v-btn
                            icon
                            density="comfortable"
                            elevation="0"
                            color="white"
                            @click.stop="resetFilters"
                        >
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                </v-toolbar>
                <v-slide-y-transition mode="out-in">
                    <v-card-text v-if="isExpanded">
                        <v-row class="mb-4"
                            ><v-col cols="12" sm="12" md="6">
                                <FormLocation
                                    :modelValue="loadingFilter"
                                    formType="ware"
                                    :title="$t('entries.filterLoadingLocation')"
                                    @update:modelValue="handleLoadingFilter"
                                    :useAsFilter="true"
                                />
                            </v-col>
                            <v-col cols="12" sm="12" md="6">
                                <FormLocation
                                    :modelValue="unloadingFilter"
                                    formType="ware"
                                    :title="
                                        $t('entries.filterUnloadingLocation')
                                    "
                                    @update:modelValue="handleUnloadingFilter"
                                    :useAsFilter="true"
                                />
                            </v-col>
                            <v-divider></v-divider>
                            <v-col cols="12" sm="6" md="3">
                                <v-text-field
                                    v-model.number="weightFilter"
                                    :label="$t('entries.filterWeight') + ' (t)'"
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    hide-details="auto"
                                    @keydown.stop
                                />
                            </v-col>

                            <!-- MDP (m²) -->
                            <v-col cols="12" sm="6" md="3">
                                <v-text-field
                                    v-model.number="mdpFilter"
                                    :label="$t('entries.filterMDP') + ' (m²)'"
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    hide-details="auto"
                                    @keydown.stop
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-divider></v-divider>
                            <v-col cols="12">
                                <v-text-field
                                    v-model.trim="identificationIdFilter"
                                    :label="$t('entries.filterId')"
                                    placeholder="TR-XXXXXXX / WR-XXXXXXX"
                                    hide-details="auto"
                                    @keydown.stop
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-divider></v-divider>
                            <v-col cols="12">
                                <v-btn color="error" class="w-100" @click="resetFilters">
                                    {{ $t("entries.clearFilters") }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-slide-y-transition>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>
import FormLocation from "@/components/forms/FormLocation.vue";
import { mapState } from "vuex";

export default {
    components: { FormLocation },
    props: {
        // initial filters from the parent
        modelValue: {
            type: Object,
            default: () => ({
                loadingLocation: { country_code: null, place_id: null, place_display_name: null },
                unloadingLocation: { country_code: null, place_id: null, place_display_name: null },
            }),
        },
        sortDirection: {
            type: String,
            default: "desc",
        },
    },
    emits: ["update:modelValue", "toggle-sort"],
    data() {
        return {
            loadingFilter: this.modelValue.loadingLocation || {
                country_code: null,
                place_id: null,
                place_display_name: null,
            },
            unloadingFilter: this.modelValue.unloadingLocation || {
                country_code: null,
                place_id: null,
                place_display_name: null,
            },
            isExpanded: false,
            weightFilter: this.modelValue.weight ?? null,
            mdpFilter: this.modelValue.mdp ?? null,
            identificationIdFilter: this.modelValue.identificationId ?? "",
        };
    },
    computed: {
        ...mapState(["countries", "isMobile"]),
        chips() {
            const list = [];

            const countryName = (code) => {
                const c = this.countries.find((c) => c.code === code);
                return c ? c.name : code;
            };

            if (this.loadingFilter.country_code) {
                list.push({
                    id: "load-country",
                    label: `${this.$t(
                        "entries.loadingFilterTitle"
                    )}: ${countryName(this.loadingFilter.country_code)}`,
                    clear: () => {
                        this.loadingFilter.country_code = null;
                        this.loadingFilter.place_id = null;
                        this.loadingFilter.place_display_name = null;
                    },
                });
            }

            if (this.loadingFilter.place_display_name) {
                list.push({
                    id: "load-place",
                    label: this.loadingFilter.place_display_name,
                    clear: () => {
                        this.loadingFilter.place_id = null;
                        this.loadingFilter.place_display_name = null;
                    },
                });
            }

            if (this.unloadingFilter.country_code) {
                list.push({
                    id: "unload-country",
                    label: `${this.$t(
                        "entries.unloadingFilterTitle"
                    )}: ${countryName(this.unloadingFilter.country_code)}`,
                    clear: () => {
                        this.unloadingFilter.country_code = null;
                        this.unloadingFilter.place_id = null;
                        this.unloadingFilter.place_display_name = null;
                    },
                });
            }

            if (this.unloadingFilter.place_display_name) {
                list.push({
                    id: "unload-place",
                    label: this.unloadingFilter.place_display_name,
                    clear: () => {
                        this.unloadingFilter.place_id = null;
                        this.unloadingFilter.place_display_name = null;
                    },
                });
            }

            if (this.weightFilter !== null) {
                list.push({
                    id: "weight",
                    label: `${this.$t("entries.weight")}: ≤ ${
                        this.weightFilter
                    } t`,
                    clear: () => (this.weightFilter = null),
                });
            }

            if (this.mdpFilter !== null) {
                list.push({
                    id: "mdp",
                    label: `${this.$t("entries.mdp")}: ≤ ${this.mdpFilter} m²`,
                    clear: () => (this.mdpFilter = null),
                });
            }

            if (this.identificationIdFilter) {
                list.push({
                    id: "ident-id",
                    label: `${this.$t("entries.filterId")}: ${
                        this.identificationIdFilter
                    }`,
                    clear: () => (this.identificationIdFilter = ""),
                });
            }
            return list;
        },
    },
    methods: {
        handleLoadingFilter(val) {
            this.loadingFilter = val;
            this.emitUpdate();
        },
        handleUnloadingFilter(val) {
            this.unloadingFilter = val;
            this.emitUpdate();
        },
        resetFilters() {
            this.loadingFilter = { country_code: null, place_id: null, place_display_name: null };
            this.unloadingFilter = { country_code: null, place_id: null, place_display_name: null };
            this.weightFilter = null;
            this.mdpFilter = null;
            this.identificationIdFilter = "";
            this.emitUpdate();
        },
        emitUpdate() {
            this.$emit("update:modelValue", {
                loadingLocation: this.loadingFilter,
                unloadingLocation: this.unloadingFilter,
                weight: this.weightFilter,
                mdp: this.mdpFilter,
                identificationId: this.identificationIdFilter || null,
            });
        },
    },
    watch: {
        // whenever a chip clears a value, re‑emit upward
        chips: {
            deep: true,
            handler() {
                this.emitUpdate();
            },
        },
        weightFilter(val) {
            if (val === "" || Number.isNaN(val)) this.weightFilter = null;
            this.emitUpdate();
        },
        mdpFilter(val) {
            if (val === "" || Number.isNaN(val)) this.mdpFilter = null;
            this.emitUpdate();
        },
        identificationIdFilter() {
            this.emitUpdate();
        },
    },
};
</script>
